/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICoreContext, IRequestContext, IImageData, IImageSettings, IGridSettings, Image, IAny, IGeneric } from '@msdyn365-commerce/core';
import { format } from '@msdyn365-commerce-modules/utilities';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { mFIGetProductRecommendationByStoreIdAsync } from '../../actions/ProductRecommendationDataActionExtension.g';
import { AttributeValue, ProductSearchResult } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getOrgUnitLocationsByArea, GetOrgUnitLocationsByAreaInput, IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import { ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { reaction, observable } from 'mobx';
import FeaturedSale from './featured-content';
import { RatingComponent } from '../../themes/mattressfirm/views/components/rating.component';
import { IMfrmProductRecommendationsData } from './mfrm-product-recommendations.data';
import { IMfrmProductRecommendationsProps } from './mfrm-product-recommendations.props.autogenerated';
import * as DataServiceEntities from '../../actions/ProductRecommendationDataServiceEntities.g';
import { GetCategoryPathsInput, getProductsCategoryPathsAction } from './../../actions/get-mfrm-products-category-paths.action';
import { IMFITryInStore, IMFITryInStoreInfoResponse } from '../../actions/TryInStoreDataServiceEntities.g';
import getTryInStoreAction, { GetTryInStoreInfoInput } from '../../actions/get-try-in-store-info.action';
import { buildProductBadge, parseBadgeData } from '../../Utilities/get-product-badge';
import { _getDeliveryDate } from '../../Utilities/product-search-result-utils';
import { getZipCode, getGeoLocationZipCode } from '../../Utilities/get-zipcode';
import { mFIATPInventoryPLPAsync } from '../../actions/BatchCoreProductDataActionExtension.g';
import { IMFIATPInventoryPLPResponse, IMFICartLine, IMFIDeliveryScheduleParameters } from '../../actions/BatchCoreProductDataServiceEntities.g';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { BVRatingSummary } from '../../Utilities/components/BVRatingSummary';
import { removeSpecialChars } from '../../Utilities/allRegex';
export interface IMfrmProductRecommendationsViewProps extends IMfrmProductRecommendationsProps<IMfrmProductRecommendationsData> {
    carouselItems: React.ReactNode[] | null;
}
export interface IFetchedCategoryIds {
    productId?: number;
    categoryId?: number;
    categoryName?: string;
}
export interface IProductAvailability {
    productId?: string;
    deliveryMessage?: string;
    availableInStore?: boolean;
}

export interface IproductInventoryData {
    StoreId?: string;
    productId?: string;
    availableInStore?: boolean;
}

export interface IProductRecommendationState {
    categoryData: IFetchedCategoryIds[];
    productData: ProductSearchResult[] | undefined;
    productAvailabilityData: IProductAvailability[];
    productInventoryData: IproductInventoryData[];
    productInventoryLoadStatus: boolean;
    storeId: string;
}
@observer
class MfrmProductRecommendations extends React.PureComponent<
IMfrmProductRecommendationsProps<IMfrmProductRecommendationsData>,
IProductRecommendationState
> {
    constructor(props: IMfrmProductRecommendationsProps<IMfrmProductRecommendationsData>) {
        super(props);

        this.state = {
            categoryData: [],
            productData: [],
            productAvailabilityData: [],
            productInventoryData: [],
            productInventoryLoadStatus: false,
            storeId: ''
        };
    }
    public async componentDidMount() {
        await this.getModuleData();

        const {
            data: { storeSelectorStateManager: storeSelectorStateManager }
        } = this.props;

        reaction(
            () => storeSelectorStateManager.result && storeSelectorStateManager.result.preferredStore?.StoreId,
            async () => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                await this.getModuleData();
            }
        );
    }

    public getModuleData = async (): Promise<void> => {
        this.setState({
            categoryData: [],
            productData: [],
            productAvailabilityData: [],
            productInventoryLoadStatus: false
        });

        const getManualProductData = async () => {
            const actionContext = this.props.context.actionContext;
            const apiSettings = actionContext.requestContext.apiSettings;
            const pIds =
                this.props.config.featuredProductList &&
                this.props.config.featuredProductList.map(function (product) {
                    return {
                        ItemId: product.productID
                    };
                });

            const pIdsSorted =
                this.props.config.featuredProductList &&
                this.props.config.featuredProductList.map(function (product) {
                    return product.productID;
                });

            const searchCriteriaInput: any = {
                Context: {
                    ChannelId: apiSettings.channelId,
                    CatalogId: apiSettings.catalogId
                },
                IncludeAttributes: true,
                SkipVariantExpansion: true,
                SearchCondition: '*',
                ItemIds: pIds
            };

            const searchResult = await ProductsDataActions.searchByCriteriaAsync(
                { callerContext: actionContext },
                searchCriteriaInput
            );
            const sortedSearchResult = searchResult.sort((a, b) => pIdsSorted.indexOf(a.ItemId) - pIdsSorted.indexOf(b.ItemId));
            this.setState({ productData: sortedSearchResult });
        };

        const categoryNameFromSlug = (this.props?.data?.category?.result?.Name && this.props?.data?.category?.result?.Name) || '';
        const displayManualProduct = this.props?.config.displayMethod === 'manual';
        try {
            if (displayManualProduct) {
                await getManualProductData();
            } else {
                var productRecommendationParam: DataServiceEntities.IMFIGetProductRecommendationByStoreIdParameters =
                {
                    NumberOfProducts: this.props.config.numberOfProducts,
                    StoreId: await this.getStoreID()
                };
                await mFIGetProductRecommendationByStoreIdAsync(
                    { callerContext: this.props.context.actionContext },
                    productRecommendationParam
                ).then(async response => {
                    if (response.ProuctdRecommendationsData && response.ProuctdRecommendationsData?.length > 3) {
                        this.setState({ productData: response.ProuctdRecommendationsData });
                    } else {
                        await getManualProductData();
                    }
                });
            }
            await this.getProductsCategories(this.props.context, this._getIsCategoryPage(), categoryNameFromSlug).then(result => {
                this.setState({
                    categoryData: result
                });
            });
            await this.getStoreInventoryData();
            await this.getDeliveryMessageData();
        } catch (error) {
            console.error('Error loading Product Recommendation Module');
        }
    };

    public getStoreInventoryData = async (): Promise<void> => {
        const { context: { app, actionContext } } = this.props;
        const ZipCode = getZipCode(actionContext) || '';
        const products = this.state.productData;
        const result: IproductInventoryData[] = [];
        const StoreId = await this.getStoreID() || '';

        products?.map(async (product) => {
            const { AttributeValues, ItemId } = product;
            const itemLinesArray: IMFICartLine[] = [];
            const lowestPriceVariantRecordId = AttributeValues?.find(
                attr => attr.Name?.trim() === app.config.EcomLowestPriceVariantRecIdKey
            )?.TextValue;
            const itemLine: IMFICartLine = {
                ItemId: ItemId,
                Quantity: 1,
                VariantRecordId: lowestPriceVariantRecordId
            };
            itemLinesArray.push(itemLine);

            const tryInStoreProductIDs: IMFITryInStore = {
                ZipCode,
                StoreId,
                Products: ItemId,
                Limit: app.config.tryInStoreLimit || 10,
                Radius: app.config.tryInStoreLookupRadius || 50
            };

            const getTryInStoreInfoInput = new GetTryInStoreInfoInput(tryInStoreProductIDs);
            await getTryInStoreAction(getTryInStoreInfoInput, actionContext).then(response => {
                const data: IMFITryInStoreInfoResponse = response;
                result.push({ StoreId: StoreId, productId: ItemId, availableInStore: data.Inventory && data.Inventory.length > 0 });
            });
            this.setState({ productInventoryData: result });
        });
    };
    public getDeliveryMessageData = async (): Promise<void> => {
        const { context: { app, actionContext } } = this.props;
        const ZipCode = getZipCode(actionContext) || '';
        const products = this.state.productData;
        const result: IProductAvailability[] = [];
        const itemLinesArray: IMFICartLine[] = [];
        products?.map(async product => {
            const { AttributeValues, ItemId } = product;
            const shippingInformation = AttributeValues?.find(attr => attr.Name?.trim() === "shippingInformation");
            if (shippingInformation?.TextValue === "Delivery") {
                const lowestPriceVariantRecordId = AttributeValues?.find(
                    attr => attr.Name?.trim() === app.config.EcomLowestPriceVariantRecIdKey
                )?.TextValue;
                const itemLine: IMFICartLine = {
                    ItemId: ItemId,
                    Quantity: 1,
                    VariantRecordId: lowestPriceVariantRecordId!
                };
                itemLinesArray.push(itemLine);
            }
        });
        const requestBody: IMFIDeliveryScheduleParameters = {
            CustomerRequestTime: dayjs().format('MM/DD/YY HH:MM:ss'),
            Id: 0,
            InventoryType: 'Delivery',
            Weeks: 6,
            StoreId: '',
            NoOfPriorityDC: 0,
            RequestedDate: dayjs().format('MM/DD/YYYY'),
            ZipCode,
            ItemLines: itemLinesArray
        };
        await mFIATPInventoryPLPAsync({ callerContext: this.props.context.actionContext }, requestBody).then((response: IMFIATPInventoryPLPResponse) => {
            const ATPInventoryPLPData = response.ATPInventoryPLPData;
            ATPInventoryPLPData?.map(product => {
                result.push({ productId: product.ItemId, deliveryMessage: _getDeliveryDate(product).deliveryByMessage, availableInStore: false });
            });
        });
        this.setState({ productAvailabilityData: result, productInventoryLoadStatus: true });
        products?.map(async product => {
            const massterProductId = product.MasterProductId ? product.MasterProductId : 0;
            const productSpecificationData: AttributeValue[] = await getAttributeValuesAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                massterProductId,
                this.props.context.request.apiSettings.channelId,
                0
            );
            const shippingInformation = productSpecificationData.find(attr => attr.Name?.trim() === "shippingInformation");
            if (shippingInformation?.TextValue === "Drop Ship") {
                var foundIndex = result.findIndex(item => item.productId === product.ItemId);
                const leadTime = productSpecificationData.find(attr => attr.Name?.trim() === "leadTime");
                const date = new Date();
                const deliveryDate = date.setDate(date.getDate());
                let firstDeliveryDate = deliveryDate;
                let lastDeliveryDate = deliveryDate;
                let firstDayInt = (leadTime?.TextValue?.split("-")[0] && parseInt(leadTime?.TextValue?.split("-")[0], 10));
                let secondDayInt = leadTime?.TextValue?.split("-")[1] && parseInt(leadTime?.TextValue?.split("-")[1], 10);
                while (firstDayInt && firstDayInt > 0 && secondDayInt && secondDayInt > 0) {
                    firstDeliveryDate = date.setDate(date.getDate() + 1);
                    if (dayjs(firstDeliveryDate).format('d') !== '0' && dayjs(firstDeliveryDate).format('d') !== '6') {
                        firstDayInt--;
                        secondDayInt--;
                    }
                }
                while (secondDayInt && secondDayInt > 0) {
                    lastDeliveryDate = date.setDate(date.getDate() + 1);
                    if (dayjs(lastDeliveryDate).format('d') !== '0' && dayjs(lastDeliveryDate).format('d') !== '6') {
                        secondDayInt--;
                    }
                }
                const newDeliveryMessage = `Delivery by ${dayjs(firstDeliveryDate).format('MMM D')} to ${dayjs(lastDeliveryDate).format('MMM D')}`;
                if (result.length > 0 && foundIndex >= 0) {
                    result[foundIndex].deliveryMessage = newDeliveryMessage;
                }
                this.setState({ productAvailabilityData: result });
            }
        });
    };

    @observable
    public getStoreID = async (): Promise<string | undefined> => {
        const {
            context: { actionContext },
            data: { storeSelectorStateManager }
        } = this.props;
        let defaultStoreId: string | undefined;
        const storeObj = localStorage.getItem('_msdyn365__preferredStore_');
        const selectedStoreID = storeObj && JSON.parse(storeObj);

        if (selectedStoreID) {
            defaultStoreId = selectedStoreID.StoreId;
        } else {
            const zipCode = getGeoLocationZipCode(actionContext) || '';

            const location = await (await storeSelectorStateManager).geoLocate(zipCode, actionContext);
            const lat = location?.latitude || 0;
            const long = location?.longitude || 0;
            const calRadius = 50;
            const distanceUnitValue = 0;
            const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(lat, long, calRadius, distanceUnitValue);

            const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
            if (orgUnitLocations) {
                defaultStoreId =
                    orgUnitLocations &&
                    orgUnitLocations[0]?.OrgUnitAvailability &&
                    orgUnitLocations[0].OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber;
            }
        }
        this.setState({ storeId: defaultStoreId ? defaultStoreId : '' });
        return defaultStoreId;
    };

    public getRatingAriaLabel = (rating?: number, ratingAriaLabel?: string): string => {
        if (rating && ratingAriaLabel) {
            const roundedRating = rating.toFixed(2);
            return format(ratingAriaLabel || '', roundedRating, '5');
        }
        return '';
    };

    public renderRating = (
        context: ICoreContext,
        typeName: string,
        id: string,
        avgRating?: number,
        totalRatings?: number,
        ariaLabel?: string
    ): JSX.Element | null => {
        if (avgRating && avgRating !== 0) {
            const numRatings = (totalRatings && totalRatings.toString()) || '0';
            const ratingAriaLabel = this.getRatingAriaLabel(avgRating, ariaLabel);

            return (
                <RatingComponent
                    context={context}
                    id={id}
                    typeName={typeName}
                    avgRating={avgRating}
                    ratingCount={numRatings}
                    readOnly
                    ariaLabel={ratingAriaLabel}
                    data={{}}
                />
            );
        } else {
            return null;
        }
    };

    public _getIsCategoryPage(): boolean {
        const { context } = this.props;
        const requestContext = context && context.request;
        const pageType =
            requestContext.urlTokens.pageType === 'Category' || (requestContext.query && requestContext.query.categoryId)
                ? 'Category'
                : 'Search';
        return pageType?.toLowerCase() === 'search' ? false : true;
    }

    public getProductsCategories = async (
        context: ICoreContext<IGeneric<IAny>>,
        isCategoryPage: boolean,
        categoryNameFromSlug: string | undefined
    ): Promise<IFetchedCategoryIds[]> => {
        if (this.state.productData && this.state.productData.length > 0) {
            let catName: string | undefined = '';
            if (isCategoryPage) {
                catName = categoryNameFromSlug;
            }
            // VSI Customization End - 42859
            const productIds: number[] = [];
            this.state.productData.forEach(prod => {
                const prodId = prod.AttributeValues?.find(attr => attr.KeyName?.trim() === 'EcomLowestPriceVariantRec')?.TextValue;
                prodId && productIds.push(Number(prodId));
            });

            const catalogId = context.actionContext.requestContext.apiSettings.catalogId;
            const productsCategories = await getProductsCategoryPathsAction(
                new GetCategoryPathsInput(context.request, catalogId, productIds),
                context.actionContext
            );
            const categoryIds: IFetchedCategoryIds[] = [];
            for (let productCategoriesIter = 0; productCategoriesIter < productsCategories.length; productCategoriesIter++) {
                if (
                    productsCategories[productCategoriesIter] &&
                    productsCategories[productCategoriesIter].CategoryPath &&
                    productsCategories[productCategoriesIter].CategoryPath!.length > 0
                ) {
                    const catPath = productsCategories[productCategoriesIter].CategoryPath;
                    const catPathSegments = catPath![0].Segments;
                    if (catPathSegments && catPathSegments.length > 0) {
                        const catId = catPathSegments.length > 0 ? catPathSegments[catPathSegments.length - 1].CategoryId : -1;
                        if (!isCategoryPage) {
                            catName = catPathSegments.length > 0 ? catPathSegments[catPathSegments.length - 1].CategoryName : '';
                        }

                        categoryIds.push({
                            productId: productsCategories[productCategoriesIter].ProductId,
                            categoryId: catId,
                            categoryName: catName
                        });
                    }
                }
            }
            return categoryIds;
        }
        return [];
    };

    public render(): JSX.Element | null {
        if (!this.state.productData) {
            return null;
        }

        const viewProps: IMfrmProductRecommendationsViewProps = {
            ...(this.props as IMfrmProductRecommendationsProps<IMfrmProductRecommendationsData>),
            carouselItems: this._getCarouselItems()
        };
        return this.props.renderView(viewProps);
    }

    private _getCarouselItems = (): React.ReactNode[] | null => {
        const storeObj = MsDyn365.isBrowser && localStorage.getItem('_mfrm__prefferedStore_');
        let preferedStore;
        if (!storeObj) {
            const {
                data: {
                    storeLocations: { result: storeLocations },
                    availabilitiesWithHours: { result: availabilitiesWithHours }
                }
            } = this.props;

            let stores: IFullOrgUnitAvailability[];
            if (storeLocations) {
                stores = storeLocations;
            } else {
                stores = availabilitiesWithHours!;
            }
            preferedStore = stores && stores.find(store => store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === this.state.storeId);
        }
        const selectedStoreCity = storeObj ? (JSON.parse(storeObj)).city : preferedStore ? preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.City : '';
        if (this.state.productData?.length === 0) {
            return null;
        }
        return this.state.productData!.map((product, index) => {
            const { Name, AverageRating, TotalRatings, Price, AttributeValues, PrimaryImageUrl, ItemId } = product;
            const { context, id, typeName, config } = this.props;
            const lowestPriceVariantRecordId = AttributeValues?.find(
                attr => attr.Name?.trim() === context.app.config.EcomLowestPriceVariantRecIdKey
            )?.TextValue;
            const isMasterProduct = product.IsMasterProduct;
            if (isMasterProduct && lowestPriceVariantRecordId !== undefined && lowestPriceVariantRecordId !== '') {
                product.RecordId = parseInt(lowestPriceVariantRecordId, 10);
            }
            const queryBasedSelection = context.app.config.queryBasedVariantSelection;
            let productUrl = getProductPageUrlSync(
                removeSpecialChars(product?.Name!, this.props.context) || '',
                queryBasedSelection ? product.MasterProductId || product.RecordId : product.RecordId,
                context && context.actionContext,
                undefined
            );
            if (queryBasedSelection) {
                const variantIdQueryParams = productUrl.includes('?') ? `&variantid=${product.RecordId}` : `?variantid=${product.RecordId}`;
                productUrl += variantIdQueryParams;
            }

            const renderProductBadge = (
                attributeName: string | undefined,
                attributes: AttributeValue[] | undefined,
                requestContext: IRequestContext
            ): JSX.Element | undefined => {
                const badgeData =
                    attributeName &&
                    attributes?.find(
                        currAttribute =>
                            currAttribute.Name && currAttribute.Name.toLowerCase().trim() === attributeName.toLowerCase().trim()
                    )?.TextValue;
                if (!badgeData) {
                    return;
                }

                // split badge data into its separate badges
                const badges = badgeData.split(',');

                // parse badge data to get its schedule and image src
                const parsedBadges = badges.map(badge => parseBadgeData(badge, requestContext));
                // build product badge image components
                const finalizedBadges = parsedBadges.map(parsedBadge => parsedBadge && buildProductBadge(parsedBadge, requestContext));

                return finalizedBadges && <>{finalizedBadges}</>;
            };

            const renderProductPlacementImage = (
                imageSettings?: IImageSettings,
                gridSettings?: IGridSettings,
                imageUrl?: string,
                altText?: string
            ): JSX.Element | null => {
                if (!imageUrl || !gridSettings || !imageSettings) {
                    return null;
                }
                const img: IImageData = {
                    src: imageUrl,
                    altText: altText ? altText : ''
                };
                const imageProps = {
                    gridSettings,
                    imageSettings
                };
                imageProps.imageSettings.cropFocalRegion = true;
                return <Image {...img} {...imageProps} loadFailureBehavior='empty' />;
            };

            const badgeAttributeToUse = context.app.config.productBadgeAttribute;
            const brand = AttributeValues?.find(attr => attr.KeyName?.trim() === 'VendorBrand')?.TextValue;
            const productVariant = AttributeValues?.find(attr => attr.Name?.trim() === 'ProductVariant')?.TextValue;

            const matchedDeliveryProduct = this.state.productAvailabilityData.find(item => item.productId === ItemId);
            const matchedInventoryProduct = this.state.productInventoryData.find(item => item.productId === ItemId);

            const deliveryInfomation = matchedDeliveryProduct?.deliveryMessage ?
                matchedDeliveryProduct?.deliveryMessage
                : 'Delivery in 6+ Weeks';

            const availableInStoreInformation = !selectedStoreCity ? undefined :
                matchedInventoryProduct?.availableInStore ? `Available to try in ${selectedStoreCity}` :
                    `Not available to try in ${selectedStoreCity}`;

            return (
                <FeaturedSale
                    key={index}
                    carouselIndex={index}
                    description={Name}
                    image={renderProductPlacementImage(
                        config.imageSettings,
                        context.request.gridSettings,
                        new URL(`${context.request.apiSettings.baseImageUrl}${PrimaryImageUrl}`).href,
                        product.Name
                    )}
                    price={Price}
                    promoBadge={renderProductBadge(badgeAttributeToUse, AttributeValues, context.request)}
                    linkTo={productUrl}
                    ratingComp={this.props.config.showBVRatings ? <BVRatingSummary itemId={ItemId} hideReadReviewsButton={true} /> : this.renderRating(context, id, typeName, AverageRating, TotalRatings, 'AriaLabel')}
                    productBrand={brand}
                    productCategory={this.state.categoryData[index]?.categoryName}
                    productID={ItemId}
                    productVariant={productVariant}
                    enhancedEcommTracking={this.props.config.enhancedEcommTracking}
                    productListName={this.props.config.productListName}
                    deliveryMessage={deliveryInfomation}
                    availableInStoreMessage={availableInStoreInformation}
                    productInventoryLoadStatus={this.state.productInventoryLoadStatus}
                />
            );
        });
    };
}

export default MfrmProductRecommendations;

